// Auto Update Service - خدمة التحديث التلقائي
// تتحقق من التحديثات تلقائياً وتدير عملية التحديث الكاملة

import { apiClient } from '../api/client';
import { API_ENDPOINTS } from '../api/endpoints';
import { getAppVersion, setAppVersion, compareVersions } from '../utils/version';

export interface UpdateStatus {
  currentVersion: string;
  latestVersion?: string;
  hasUpdate: boolean;
  latestManifest?: UpdateManifest;
  updateServerConfigured: boolean;
  message?: string;
}

export interface UpdateManifest {
  version: string;
  buildDate: string;
  packageUrl: string;
  packageSha256: string;
  updateType?: string;
  requiresDatabaseUpdate: boolean;
  releaseNotes?: ReleaseNotes;
}

export interface ReleaseNotes {
  version: string;
  releaseDate: string;
  description?: string;
  descriptionEn?: string;
  newFeatures?: string[];
  bugFixes?: string[];
  improvements?: string[];
  breakingChanges?: string[];
  notes?: string[];
}

export interface UpdateProgress {
  stage: string;
  message: string;
  percentage: number;
  isComplete: boolean;
  hasError: boolean;
  errorMessage?: string;
}

export interface InstallUpdateResponse {
  success: boolean;
  message?: string;
  newVersion?: string;
  backupResult?: any;
  extractionResult?: any;
  migrationResult?: any;
  deploymentResult?: any;
  healthCheckResult?: any;
  restartFailed?: boolean;
}

class AutoUpdateService {
  private checkInterval: number = 5 * 60 * 1000; // 5 minutes
  private checkOnStartup: boolean = true;
  private autoInstallEnabled: boolean = false;
  private updateListeners: Array<(status: UpdateStatus) => void> = [];
  private progressListeners: Array<(progress: UpdateProgress) => void> = [];
  private intervalId: NodeJS.Timeout | null = null;

  /**
   * Initialize auto-update service
   */
  async initialize(): Promise<void> {
    const status = await this.checkForUpdates();
    if (!status.updateServerConfigured) {
      return;
    }
    this.startPeriodicCheck();
  }

  /**
   * Check for available updates
   */
  async checkForUpdates(): Promise<UpdateStatus> {
    try {
      const currentVersion = getAppVersion();
      const response = await apiClient.get<UpdateStatus>(API_ENDPOINTS.update.status);
      const status = response.data;

      // Never treat equal/older remote versions as updates
      if (
        status.hasUpdate &&
        status.latestVersion &&
        compareVersions(status.latestVersion, status.currentVersion || currentVersion) <= 0
      ) {
        status.hasUpdate = false;
      }
      
      // Notify listeners
      this.notifyStatusListeners(status);
      
      return status;
    } catch (error) {
      console.error('Error checking for updates:', error);
      return {
        currentVersion: getAppVersion(),
        hasUpdate: false,
        updateServerConfigured: false,
        message: 'فشل التحقق من التحديثات'
      };
    }
  }

  /**
   * Download update package
   */
  async downloadUpdate(version?: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.update.download, { version });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || 'فشل تحميل التحديث'
      };
    }
  }

  /**
   * Create backup before update
   */
  async createBackup(options?: {
    backupPath?: string;
    backupFrontend?: boolean;
    backupBackend?: boolean;
    backupDatabase?: boolean;
  }): Promise<{ success: boolean; message?: string; backupPath?: string }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.update.backup, options || {});
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || 'فشل إنشاء النسخة الاحتياطية'
      };
    }
  }

  /**
   * Install update automatically
   */
  async installUpdate(options: {
    version: string;
    createBackup?: boolean;
    backupDatabase?: boolean;
    restartService?: boolean;
  }): Promise<InstallUpdateResponse> {
    try {
      // Update progress: Starting
      this.notifyProgressListeners({
        stage: 'Starting',
        message: 'جارٍ بدء عملية التحديث...',
        percentage: 0,
        isComplete: false,
        hasError: false
      });

      // Step 1: Download if needed
      this.notifyProgressListeners({
        stage: 'Downloading',
        message: 'جارٍ تحميل التحديث...',
        percentage: 10,
        isComplete: false,
        hasError: false
      });

      const downloadResult = await this.downloadUpdate(options.version);
      if (!downloadResult.success) {
        throw new Error(downloadResult.message || 'فشل تحميل التحديث');
      }

      // Step 2: Install
      this.notifyProgressListeners({
        stage: 'Installing',
        message: 'جارٍ تثبيت التحديث...',
        percentage: 30,
        isComplete: false,
        hasError: false
      });

      const installResponse = await apiClient.post<InstallUpdateResponse>(
        API_ENDPOINTS.update.install,
        {
          version: options.version,
          createBackup: options.createBackup ?? true,
          backupDatabase: options.backupDatabase ?? true,
          restartService: options.restartService ?? true,
        }
      );

      const result = installResponse.data;

      // Poll for progress
      await this.pollUpdateProgress();

      if (result.success && result.newVersion) {
        // Update local version
        setAppVersion(result.newVersion);
        
        this.notifyProgressListeners({
          stage: 'Complete',
          message: 'تم التحديث بنجاح! سيتم إعادة تشغيل التطبيق...',
          percentage: 100,
          isComplete: true,
          hasError: false
        });

        // Reload page after a delay
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        this.notifyProgressListeners({
          stage: 'Failed',
          message: result.message || 'فشل التحديث',
          percentage: 0,
          isComplete: false,
          hasError: true,
          errorMessage: result.message
        });
      }

      return result;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'حدث خطأ أثناء التحديث';
      
      this.notifyProgressListeners({
        stage: 'Failed',
        message: errorMessage,
        percentage: 0,
        isComplete: false,
        hasError: true,
        errorMessage
      });

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * Poll for update progress
   */
  private async pollUpdateProgress(): Promise<void> {
    const maxAttempts = 300; // 5 minutes max
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await apiClient.get<UpdateProgress>(API_ENDPOINTS.update.progress);
        const progress = response.data;
        
        this.notifyProgressListeners(progress);

        if (progress.isComplete || progress.hasError) {
          break;
        }

        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        attempts++;
      } catch (error) {
        console.error('Error polling update progress:', error);
        break;
      }
    }
  }

  /**
   * Rollback to previous version
   */
  async rollback(backupPath: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.update.rollback, { backupPath });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || 'فشل استعادة النسخة السابقة'
      };
    }
  }

  /**
   * Start periodic update check
   */
  startPeriodicCheck(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = setInterval(() => {
      this.checkForUpdates();
    }, this.checkInterval);
  }

  /**
   * Stop periodic update check
   */
  stopPeriodicCheck(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Add update status listener
   */
  onUpdateStatus(callback: (status: UpdateStatus) => void): () => void {
    this.updateListeners.push(callback);
    return () => {
      const index = this.updateListeners.indexOf(callback);
      if (index > -1) {
        this.updateListeners.splice(index, 1);
      }
    };
  }

  /**
   * Add progress listener
   */
  onProgress(callback: (progress: UpdateProgress) => void): () => void {
    this.progressListeners.push(callback);
    return () => {
      const index = this.progressListeners.indexOf(callback);
      if (index > -1) {
        this.progressListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify status listeners
   */
  private notifyStatusListeners(status: UpdateStatus): void {
    this.updateListeners.forEach(listener => listener(status));
  }

  /**
   * Notify progress listeners
   */
  private notifyProgressListeners(progress: UpdateProgress): void {
    this.progressListeners.forEach(listener => listener(progress));
  }

  /**
   * Get current progress
   */
  async getCurrentProgress(): Promise<UpdateProgress> {
    try {
      const response = await apiClient.get<UpdateProgress>(API_ENDPOINTS.update.progress);
      return response.data;
    } catch (error) {
      return {
        stage: 'Idle',
        message: 'لا توجد عملية تحديث نشطة',
        percentage: 0,
        isComplete: false,
        hasError: false
      };
    }
  }
}

// Export singleton instance
export const autoUpdateService = new AutoUpdateService();


